
'use client';

import { useState, useMemo } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  addWeeks,
  subWeeks,
  parseISO,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Birthday } from '@/lib/types';
import { BirthdayCard } from './birthday-card';
import { cn } from '@/lib/utils';

interface WeeklyViewProps {
  birthdays: Birthday[];
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
}

export function WeeklyView({
  birthdays,
  selectedDate: initialSelectedDate,
  onDateSelect,
}: WeeklyViewProps) {
  const [currentDate, setCurrentDate] = useState(initialSelectedDate || new Date());

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const birthdaysByDay = useMemo(() => {
    const map = new Map<string, Birthday[]>();
    days.forEach((day) => {
      const dayKey = format(day, 'yyyy-MM-dd');
      const dayBirthdays = birthdays.filter((b) => {
        const dob = parseISO(b.dob);
        return dob.getDate() === day.getDate() && dob.getMonth() === day.getMonth();
      });
      if (dayBirthdays.length > 0) {
        map.set(dayKey, dayBirthdays);
      }
    });
    return map;
  }, [days, birthdays]);

  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const selectedDayBirthdays = useMemo(() => {
     if (!initialSelectedDate) return [];
     const dayKey = format(initialSelectedDate, 'yyyy-MM-dd');
     return birthdaysByDay.get(dayKey) || [];
  },[initialSelectedDate, birthdaysByDay]);


  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_400px] gap-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {format(weekStart, 'MMMM yyyy')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={goToNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 border-t border-l">
            {days.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const dayBirthdays = birthdaysByDay.get(dayKey) || [];
              const isSelected = initialSelectedDate && isSameDay(day, initialSelectedDate);

              return (
                <div
                  key={dayKey}
                  className={cn(
                    'border-r border-b p-2 h-40 flex flex-col cursor-pointer hover:bg-accent/50',
                    isSelected && 'bg-accent'
                  )}
                  onClick={() => onDateSelect(day)}
                >
                  <div className={cn(
                      "font-semibold",
                      isSameDay(day, new Date()) && "text-primary font-bold"
                    )}>
                    {format(day, 'd')}
                  </div>
                  <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
                  <div className="mt-2 space-y-1 overflow-auto">
                    {dayBirthdays.map((b) => (
                      <div
                        key={b.id}
                        className="bg-primary/20 text-primary-foreground rounded-md p-1 text-xs truncate"
                      >
                        {b.name}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
       <div className="space-y-4">
        <Card>
            <CardHeader>
            <CardTitle>
                {initialSelectedDate
                ? `Birthdays on ${format(initialSelectedDate, 'MMMM d')}`
                : 'Select a day'}
            </CardTitle>
            </CardHeader>
            <CardContent>
            {initialSelectedDate ? (
                selectedDayBirthdays.length > 0 ? (
                <div className="space-y-4">
                    {selectedDayBirthdays.map((b) => (
                    <BirthdayCard key={b.id} birthday={b} />
                    ))}
                </div>
                ) : (
                <p className="text-sm text-muted-foreground">
                    No birthdays on this day.
                </p>
                )
            ) : (
                <p className="text-sm text-muted-foreground">
                Select a day to see birthdays.
                </p>
            )}
            </CardContent>
        </Card>
        </div>
    </div>
  );
}
